import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { Diagram, UnifiedDiagramData, DiagramNode, DiagramEdge } from '@veodiagram/shared';
import { api } from '../services/api';
import { DiagramViewer } from '../components/diagram/DiagramViewer';

/**
 * Format a date for display (Issue #158)
 */
function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

// Valid node types for the base Diagram schema
const VALID_NODE_TYPES = new Set([
  'deployment', 'codeBlock', 'storage', 'queue', 'external',
  'database', 'cache', 'api', 'frontend', 'service',
]);

// Valid edge types for the base Diagram schema
const VALID_EDGE_TYPES = new Set(['default', 'http', 'async', 'database', 'file']);

/**
 * Convert API Diagram to UnifiedDiagramData format for DiagramViewer
 * Issue #284: Handle diagrams with missing graph data gracefully
 */
function convertToUnifiedData(diagram: Diagram): UnifiedDiagramData | null {
  // Issue #284: Return null if diagram has no graph data
  if (!diagram.graph?.nodes || !diagram.graph?.edges) {
    return null;
  }
  return {
    metadata: {
      title: diagram.name,
      description: diagram.description || undefined,
      diagramType: diagram.type as UnifiedDiagramData['metadata']['diagramType'],
      layoutDirection: 'TB',
      layoutAlgorithm: 'dagre',
    },
    nodes: diagram.graph.nodes.map((node) => ({
      id: node.id,
      type: node.type,
      label: node.label,
      description: node.description,
      position: node.position,
      style: node.style,
      data: node.data,
    })),
    edges: diagram.graph.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      type: edge.type || 'default',
      animated: edge.animated ?? false,
      data: edge.data,
    })),
  };
}

/**
 * Convert UnifiedDiagramData back to Diagram graph format for saving
 */
function convertFromUnifiedData(data: UnifiedDiagramData): Diagram['graph'] {
  const nodes: DiagramNode[] = data.nodes.map((node) => ({
    id: node.id,
    // Map extended types back to base types
    type: (VALID_NODE_TYPES.has(node.type) ? node.type : 'service') as DiagramNode['type'],
    label: node.label,
    description: node.description,
    position: node.position,
    style: node.style ? {
      width: node.style.borderWidth,
      height: undefined,
      backgroundColor: node.style.backgroundColor,
      borderColor: node.style.borderColor,
    } : undefined,
    data: node.data,
  }));

  const edges: DiagramEdge[] = data.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.label,
    // Map extended types back to base types
    type: (VALID_EDGE_TYPES.has(edge.type) ? edge.type : 'default') as DiagramEdge['type'],
    animated: edge.animated,
    data: edge.data,
  }));

  return { nodes, edges };
}

export function DiagramPage() {
  const { diagramId } = useParams<{ diagramId: string }>();
  const navigate = useNavigate();
  const [diagram, setDiagram] = useState<Diagram | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Issue #158: Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState<Diagram[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  // Issue #239/#241: Regenerate diagram state with detailed progress tracking
  interface RegenerationState {
    jobId: string;
    startTime: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    error?: string;
  }
  const [regenerationState, setRegenerationState] = useState<RegenerationState | null>(null);
  // Issue #241: Force re-renders to update elapsed time
  const [, setTickForElapsed] = useState(0);

  useEffect(() => {
    if (diagramId) {
      loadDiagram();
    }
  }, [diagramId]);

  // Redirect diagram types that have dedicated explorer pages.
  // #1074 D1: `frontendCallGraph` and `backendCallGraph` diagrams store their
  // payload in `graph.metadata.{frontendCallGraph|backendCallGraph}` rather
  // than `graph.nodes`/`graph.edges`, so the generic DiagramPage renders the
  // "No Evidence Found for the *Diagram* diagram" empty state. The dedicated
  // explorer pages know how to read the metadata shape — send users there.
  useEffect(() => {
    if (!diagram?.projectId) return;
    if (diagram.type === 'databaseSchema') {
      navigate(`/projects/${diagram.projectId}/schema`, { replace: true });
    } else if (diagram.type === 'frontendCallGraph') {
      navigate(`/projects/${diagram.projectId}/frontend-graph`, { replace: true });
    } else if (diagram.type === 'backendCallGraph') {
      navigate(`/projects/${diagram.projectId}/backend-graph`, { replace: true });
    }
  }, [diagram, navigate]);

  // Issue #241: Force re-renders while regeneration is in progress to update elapsed time
  useEffect(() => {
    if (!regenerationState) return;
    const interval = setInterval(() => {
      setTickForElapsed(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [regenerationState]);

  const loadDiagram = async () => {
    try {
      const data = await api.getDiagram(diagramId!);
      setDiagram(data);
    } catch (err) {
      console.error('Failed to load diagram:', err);
      setError('Failed to load diagram');
    } finally {
      setIsLoading(false);
    }
  };

  // Convert diagram to unified format for DiagramViewer
  const unifiedData = useMemo(() => {
    if (!diagram) return null;
    return convertToUnifiedData(diagram);
  }, [diagram]);

  // Handle data changes from DiagramViewer
  const handleDataChange = useCallback(
    async (updatedData: UnifiedDiagramData) => {
      if (!diagramId || !diagram) return;
      setIsSaving(true);
      try {
        const graph = convertFromUnifiedData(updatedData);
        const saved = await api.updateDiagram(diagramId, { graph });
        setDiagram(saved);
      } catch (err) {
        console.error('Failed to save diagram:', err);
        setError('Failed to save diagram');
      } finally {
        setIsSaving(false);
      }
    },
    [diagramId, diagram]
  );

  // Handle errors from DiagramViewer
  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setTimeout(() => setError(null), 5000);
  }, []);

  // Issue #158: Version history handlers
  const loadVersions = useCallback(async () => {
    if (!diagramId) return;
    setIsLoadingVersions(true);
    try {
      const data = await api.getDiagramVersions(diagramId);
      setVersions(data);
    } catch (err) {
      console.error('Failed to load versions:', err);
      setError('Failed to load version history');
    } finally {
      setIsLoadingVersions(false);
    }
  }, [diagramId]);

  const handleShowVersionHistory = useCallback(async () => {
    setShowVersionHistory(true);
    await loadVersions();
  }, [loadVersions]);

  // Issue #239/#241: Handle diagram regeneration with detailed progress tracking
  const handleRegenerate = useCallback(async () => {
    if (!diagramId) return;
    setError(null);

    try {
      const result = await api.regenerateDiagram(diagramId);

      // Issue #241: Initialize detailed regeneration state
      setRegenerationState({
        jobId: result.jobId,
        startTime: Date.now(),
        status: 'pending',
        progress: 0,
      });

      // Poll for completion with progress updates
      const pollInterval = setInterval(async () => {
        try {
          const job = await api.getAnalysisStatus(result.jobId);

          if (job.status === 'completed') {
            clearInterval(pollInterval);
            // Show 100% briefly before clearing
            setRegenerationState(prev => prev ? { ...prev, status: 'completed', progress: 100 } : null);
            // Reload the diagram to get the updated version
            await loadDiagram();
            // Clear state after brief delay
            setTimeout(() => setRegenerationState(null), 500);
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            setRegenerationState(prev => prev ? {
              ...prev,
              status: 'failed',
              error: job.error || 'Unknown error',
            } : null);
            // Clear error state after 5 seconds
            setTimeout(() => setRegenerationState(null), 5000);
          } else {
            // Update progress
            setRegenerationState(prev => prev ? {
              ...prev,
              status: job.status as 'pending' | 'processing',
              progress: job.progress || 0,
            } : null);
          }
        } catch {
          // Continue polling on error
        }
      }, 1500);

      // Stop polling after 5 minutes (timeout)
      setTimeout(() => {
        clearInterval(pollInterval);
        setRegenerationState(prev => {
          if (prev && prev.status !== 'completed') {
            return { ...prev, status: 'failed', error: 'Timed out' };
          }
          return prev;
        });
        // Clear after showing timeout
        setTimeout(() => setRegenerationState(null), 5000);
      }, 300000);
    } catch (err) {
      console.error('Failed to start regeneration:', err);
      setRegenerationState({
        jobId: '',
        startTime: Date.now(),
        status: 'failed',
        progress: 0,
        error: err instanceof Error ? err.message : 'Failed to start regeneration',
      });
      // Clear error state after 5 seconds
      setTimeout(() => setRegenerationState(null), 5000);
    }
  }, [diagramId, loadDiagram]);

  // Issue #158 Phase 7: Version comparison helper
  // Note: The versions API returns only metadata (id, version, isLatest, dates)
  // without the full graph data, so we can't compute diffs for historical versions.
  const getVersionDiff = useCallback((version: Diagram): { nodesDiff: number; edgesDiff: number } | null => {
    if (!diagram?.graph?.nodes || !version?.graph?.nodes) return null;
    const currentNodes = diagram.graph.nodes.length;
    const currentEdges = diagram.graph.edges?.length || 0;
    const versionNodes = version.graph.nodes.length;
    const versionEdges = version.graph.edges?.length || 0;
    return {
      nodesDiff: currentNodes - versionNodes,
      edgesDiff: currentEdges - versionEdges,
    };
  }, [diagram]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <p className="text-muted-foreground">Loading diagram...</p>
      </div>
    );
  }

  if (!diagram || !unifiedData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center text-muted-foreground">Diagram not found</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Issue #284: Historical version banner */}
      {diagram.isLatest === false && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 text-center">
          <span className="text-sm text-amber-700 dark:text-amber-400">
            You are viewing version {diagram.version} (historical). This is not the latest version.
          </span>
        </div>
      )}
      {/* Navigation bar */}
      <div className="border-b border-border px-4 py-2 flex items-center justify-between bg-background">
        <div className="flex items-center gap-4">
          <Link
            to={`/projects/${diagram.projectId}`}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; Back to Project
          </Link>
          {/* Issue #462: Link to Call Graph Explorer for call graph diagrams */}
          {diagram.type === 'callGraph' && (
            <Link
              to={`/projects/${diagram.projectId}/call-graph`}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Open Explorer
            </Link>
          )}
          {isSaving && (
            <span className="text-xs text-muted-foreground">Saving...</span>
          )}
          {error && (
            <span className="text-xs text-destructive">{error}</span>
          )}
        </div>
        {/* Version info, regenerate, and history buttons (Issue #158, #239) */}
        <div className="flex items-center gap-3">
          {diagram.version && diagram.version > 1 && (
            <span className="text-xs text-muted-foreground">
              v{diagram.version}
            </span>
          )}
          {/* Issue #239/#241: Regenerate button with progress feedback */}
          {regenerationState ? (
            <div className="inline-flex items-center gap-2 px-2.5 py-1.5 text-sm bg-muted/50 rounded-md">
              {regenerationState.status === 'failed' ? (
                <>
                  <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-red-600 dark:text-red-400 font-medium truncate max-w-[150px]" title={regenerationState.error}>
                    {regenerationState.error || 'Failed'}
                  </span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-muted-foreground">
                    {regenerationState.progress > 0 ? `${regenerationState.progress}%` : 'Starting...'}
                  </span>
                  <span className="text-muted-foreground/70 text-xs">
                    {(() => {
                      const elapsedSecs = Math.floor((Date.now() - regenerationState.startTime) / 1000);
                      return elapsedSecs < 60
                        ? `${elapsedSecs}s`
                        : `${Math.floor(elapsedSecs / 60)}m ${elapsedSecs % 60}s`;
                    })()}
                  </span>
                </>
              )}
            </div>
          ) : (
            <button
              onClick={handleRegenerate}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
              title="Regenerate this diagram"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Regenerate
            </button>
          )}
          <button
            onClick={handleShowVersionHistory}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
            title="View version history"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            History
          </button>
        </div>
      </div>

      {/* DiagramViewer with interactive rendering and export */}
      <div className="flex-1 relative">
        <DiagramViewer
          data={unifiedData}
          readOnly={false}
          onDataChange={handleDataChange}
          onError={handleError}
        />

        {/* Version History Panel (Issue #158) */}
        {showVersionHistory && (
          <>
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/20 z-10"
              onClick={() => setShowVersionHistory(false)}
            />
            {/* Panel */}
            <div className="absolute right-0 top-0 bottom-0 w-80 bg-background border-l border-border shadow-lg z-20 flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h3 className="font-semibold">Version History</h3>
                <button
                  onClick={() => setShowVersionHistory(false)}
                  className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {isLoadingVersions ? (
                  <div className="flex items-center justify-center py-8">
                    <svg className="w-6 h-6 animate-spin text-muted-foreground" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                ) : versions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <svg className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm">No version history</p>
                    <p className="text-xs mt-1">Versions are created when changes are saved.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Current version */}
                    <div className="p-3 rounded-lg border border-primary bg-primary/5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">v{diagram.version || 1}</span>
                        <span className="text-xs text-primary font-medium">Current</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(diagram.updatedAt)}
                      </p>
                      {diagram.graph?.nodes && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {diagram.graph.nodes.length} nodes, {diagram.graph.edges?.length || 0} edges
                        </p>
                      )}
                    </div>
                    {/* Previous versions */}
                    {versions.map((version) => {
                      const diff = getVersionDiff(version);
                      return (
                        <div
                          key={version.id}
                          className="p-3 rounded-lg border border-border hover:border-muted-foreground/30 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">v{version.version}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(version.updatedAt)}
                            </span>
                          </div>
                          {/* Version metadata - graph data not available from versions API */}
                          <p className="text-xs text-muted-foreground mb-1">
                            Version {version.version}
                          </p>
                          {/* Version comparison (Issue #158 Phase 7) */}
                          {diff && (diff.nodesDiff !== 0 || diff.edgesDiff !== 0) && (
                            <div className="flex items-center gap-2 text-xs mb-2">
                              <span className="text-muted-foreground">vs current:</span>
                              {diff.nodesDiff !== 0 && (
                                <span className={diff.nodesDiff > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                  {diff.nodesDiff > 0 ? '+' : ''}{diff.nodesDiff} nodes
                                </span>
                              )}
                              {diff.edgesDiff !== 0 && (
                                <span className={diff.edgesDiff > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                  {diff.edgesDiff > 0 ? '+' : ''}{diff.edgesDiff} edges
                                </span>
                              )}
                            </div>
                          )}
                          {/* Issue #284: View button for historical versions */}
                          <a
                            href={`/diagrams/${version.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded hover:bg-muted hover:text-foreground transition-colors text-center"
                          >
                            View
                          </a>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
